import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "./useApi";
import { ImportData } from "./ImportData";
import { AuditoriaView } from "./ListViews";

const FIELDS = [["whatsapp", "Número de WhatsApp (con indicativo 57, ej. 573166919233)"], ["banco", "Banco"], ["cuenta", "Cuenta (tipo y número)"], ["llave", "Llave Bre-B"], ["nit", "NIT"]];

const SettingsForm = () => {
  const { settings, setSettings } = useSettings();
  const [f, setF] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, settings[k] || ""])));
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put("/settings", f); setSettings(data); setF((x) => ({ ...x, whatsapp: data.whatsapp })); toast.success("Datos guardados"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/settings/qr", fd); setSettings(data); toast.success("Código QR actualizado"); }
    catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };
  const removeQr = async () => {
    try { const { data } = await api.delete("/settings/qr"); setSettings(data); toast.success("Código QR eliminado"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form className="panel" onSubmit={save} data-testid="settings-form">
      <h4>Datos de contacto y donaciones</h4>
      <p className="note">Se muestran en la sección Donar y en los botones de WhatsApp del sitio. Escribe “Por confirmar” si aún no los tienes.</p>
      <div className="fgrid2">
        {FIELDS.map(([k, l]) => (
          <label key={k}>{l}<input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}-input`} /></label>
        ))}
      </div>
      <button className="btn btn-sol" type="submit" disabled={busy} style={{ marginTop: 14 }} data-testid="settings-save-btn">{busy ? "Guardando…" : "Guardar datos"}</button>
      <h4 style={{ marginTop: 22 }}>Código QR de donaciones</h4>
      {settings.qr ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/qr/image?v=${settings.qr_version}`} alt="QR actual" style={{ width: 160, borderRadius: 12, background: "#fff", marginBottom: 10 }} data-testid="settings-qr-preview" /> : <p className="note" data-testid="settings-qr-empty">Por confirmar (aún no se ha subido).</p>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={upload} style={{ maxWidth: 340 }} data-testid="settings-qr-input" />
        {settings.qr && <button type="button" className="btn" style={{ background: "#1E4F87", color: "#fff" }} onClick={removeQr} data-testid="settings-qr-delete-btn">Quitar QR</button>}
      </div>
    </form>
  );
};

const U0 = { name: "", email: "", password: "", role: "psicologo" };

const UserRow = ({ u, self, onToggle }) => (
  <tr data-testid={`user-row-${u.email}`} style={u.active ? undefined : { opacity: 0.6 }}>
    <td>{u.name}</td><td>{u.email}</td><td>{u.role === "admin" ? "Administrador" : "Psicólogo"}</td>
    <td><span className={"badge" + (u.active ? " y" : "")} data-testid={`user-status-${u.email}`}>{u.active ? "Activa" : "Desactivada"}</span></td>
    <td>{self ? <span className="note">Tú</span> : (
      <button className="chip" onClick={() => onToggle(u)} data-testid={`user-toggle-${u.email}`}>{u.active ? "Desactivar" : "Activar"}</button>
    )}</td>
  </tr>
);

const Users = () => {
  const { user } = useAuth();
  const { data, setData } = useApi("/users");
  const toggle = async (u) => {
    if (u.active && !window.confirm(`¿Desactivar la cuenta de ${u.email}? No podrá iniciar sesión.`)) return;
    try {
      const { data: nu } = await api.patch(`/users/${u.id}`, { active: !u.active });
      setData(data.map((x) => (x.id === nu.id ? nu : x)));
      toast.success(nu.active ? "Cuenta activada" : "Cuenta desactivada");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const [f, setF] = useState(U0);
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data: u } = await api.post("/users", f); setData([...(data || []), u]); setF(U0); toast.success("Usuario creado"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel" data-testid="users-panel">
      <h4>Usuarios del equipo</h4>
      <div className="wrapx">
        <table className="tbl" data-testid="users-table">
          <thead><tr><th>Nombre</th><th>Correo</th><th>Rol</th><th>Estado</th><th></th></tr></thead>
          <tbody>{(data || []).map((u) => <UserRow key={u.id} u={u} self={u.id === user.id} onToggle={toggle} />)}</tbody>
        </table>
      </div>
      <form onSubmit={create} style={{ marginTop: 16 }} data-testid="user-create-form">
        <div className="fgrid2">
          <label>Nombre<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required minLength={2} data-testid="user-name-input" /></label>
          <label>Correo<input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="user-email-input" /></label>
          <label>Contraseña (mínimo 8 caracteres)<input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={8} autoComplete="new-password" data-testid="user-password-input" /></label>
          <label>Rol<select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-role-select"><option value="psicologo">Psicólogo</option><option value="admin">Administrador</option></select></label>
        </div>
        <button className="btn btn-sol" type="submit" disabled={busy} style={{ marginTop: 14 }} data-testid="user-create-btn">{busy ? "Creando…" : "Crear usuario"}</button>
      </form>
    </div>
  );
};

export const AdminView = () => (
  <div data-testid="admin-view">
    <ImportData />
    <SettingsForm />
    <Users />
    <AuditoriaView />
  </div>
);
